import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, remove, update } from 'firebase/database';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';
import './AdminApproval.css'; // Using the new stylesheet
import { FaSpinner } from 'react-icons/fa';

const ApproveUser = () => {
    const { userData } = useAuth();
    const [pendingUsers, setPendingUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [processingId, setProcessingId] = useState(null); // To track which user is being processed

    useEffect(() => {
        if (userData?.role !== 'admin') {
            setLoading(false);
            setError('You do not have permission to view this page.');
            return;
        }

        const db = getDatabase();
        const pendingUsersRef = ref(db, 'pending_users');

        const unsubscribe = onValue(pendingUsersRef, (snapshot) => {
            setLoading(true);
            if (snapshot.exists()) {
                const data = snapshot.val();
                const userList = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                }));
                setPendingUsers(userList);
            } else {
                setPendingUsers([]);
            }
            setLoading(false);
        }, (err) => {
            setError(`Failed to fetch pending users: ${err.message}`);
            setLoading(false);
        });

        return () => unsubscribe(); // Cleanup listener on unmount
    }, [userData]);

    const handleApprove = async (pendingUser) => {
        if (!window.confirm(`Are you sure you want to approve ${pendingUser.fullName}?`)) return;

        setProcessingId(pendingUser.id);
        setError('');

        const auth = getAuth();
        const db = getDatabase();

        try {
            // Step 1: Create the user in Firebase Authentication
            const userCredential = await createUserWithEmailAndPassword(auth, pendingUser.email, pendingUser.password);
            const newUser = userCredential.user;

            // Step 2: Create the user data object to be saved in the main /users node
            const newUser_data = { ...pendingUser };
            delete newUser_data.password; // Remove the password before saving to the public user profile
            delete newUser_data.id;       // Remove the temporary pending ID
            newUser_data.status = 'active';
            newUser_data.uid = newUser.uid; // Add the official Firebase Auth UID

            // Step 3: Save the new user's data to the /users and /user_search_records nodes
            const updates = {};
            updates[`users/${newUser.uid}`] = newUser_data;
            updates[`user_search_records/${newUser.uid}`] = {
                fullName: pendingUser.fullName,
                email: pendingUser.email,
                regNo: pendingUser.regNo, // Assuming regNo is collected at registration
                className: pendingUser.className
            };
            await update(ref(db), updates);

            // Step 4: Remove the user from the /pending_users node
            await remove(ref(db, `pending_users/${pendingUser.id}`));

            alert(`${pendingUser.fullName} has been approved and their account is now active.`);

        } catch (err) {
            setError(`Error approving user: ${err.message}`);
            alert(`An error occurred while approving ${pendingUser.fullName}. Please check the console for details.`);
        } finally {
            setProcessingId(null);
        }
    };

    const handleReject = async (pendingUser) => {
        if (!window.confirm(`Are you sure you want to REJECT ${pendingUser.fullName}? This cannot be undone.`)) return;
        
        setProcessingId(pendingUser.id);
        setError('');

        try {
            const db = getDatabase();
            await remove(ref(db, `pending_users/${pendingUser.id}`));
            // Note: You might also want to delete their profile picture from Storage here.

            alert(`${pendingUser.fullName}'s application has been rejected and removed.`);

        } catch (err) {
            setError(`Error rejecting user: ${err.message}`);
        } finally {
            setProcessingId(null);
        }
    };

    if (userData?.role !== 'admin') {
        return <div className="admin-approval-container"><p>{error}</p></div>;
    }

    if (loading) {
        return <div className="loading-container"><FaSpinner className="spinner"/> Loading Applications...</div>;
    }

    return (
        <div className="admin-approval-container">
            <h2>Pending User Registrations</h2>
            {error && <p className="alert alert-danger">{error}</p>}
            
            {pendingUsers.length === 0 ? (
                <div className="empty-container">
                    <p>There are no pending user applications at this time.</p>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="pending-users-table">
                        <thead>
                            <tr>
                                <th>Profile</th>
                                <th>User Details</th>
                                <th>Role</th>
                                <th>Submitted</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingUsers.map(user => (
                                <tr key={user.id}>
                                    <td><img src={user.profilePicUrl} alt={user.fullName} className="user-profile-pic" /></td>
                                    <td>
                                        <ul className="user-details-list">
                                            <li><strong>Name:</strong> {user.fullName}</li>
                                            <li><strong>Email:</strong> {user.email}</li>
                                            <li><strong>Phone:</strong> {user.phoneNumber}</li>
                                        </ul>
                                    </td>
                                    <td><span className="badge bg-secondary">{user.role}</span></td>
                                    <td>{new Date(user.submittedAt).toLocaleDateString()}</td>
                                    <td className="action-buttons">
                                        <button 
                                            className="btn-approve" 
                                            onClick={() => handleApprove(user)} 
                                            disabled={processingId === user.id}
                                        >
                                            {processingId === user.id ? <FaSpinner className="spinner"/> : 'Approve'}
                                        </button>
                                        <button 
                                            className="btn-reject" 
                                            onClick={() => handleReject(user)} 
                                            disabled={processingId === user.id}
                                        >
                                            {processingId === user.id ? <FaSpinner className="spinner"/> : 'Reject'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ApproveUser;
